import { StepperSelectionEvent } from '@angular/cdk/stepper'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { MatStepper } from '@angular/material/stepper'
import { ProfilesEntity } from '@galabid/gb-dashboard/user/data-access'

import { EventTicketPurchasesFacade } from 'gb-dashboard/event-tickets/data-access-event-ticket-purchases'
import * as EventTicketPurchasesActions from 'libs/gb-dashboard/event-tickets/data-access-event-ticket-purchases/src/lib/+state/event-ticket-purchases.actions'
import {
  PaymentMethodFeeTypeEnum,
  PaymentMethodsEntity,
} from 'libs/gb-dashboard/setup/data-access-payment-methods/src/lib/+state/payment-methods.models'
import { IconLibrary } from 'libs/gb-dashboard/shared/ui/src/lib/gb-icon/gb-icon-library.models'
import { PaymentProcessEnum } from 'libs/shared/models/payment-method/payment-method.models'
import { GbEventTicketPurchaseStepperViewController } from './gb-event-ticket-purchase-stepper-view-controller.service'
import * as StripeActions from 'libs/gb-dashboard/shared/data-access-stripe/src/lib/+state/stripe.actions'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { EventTicketPackagesFacade } from '@galabid/gb-dashboard/event-tickets/data-access-event-ticket-packages'
import { TaxesEntity, TaxesFacade, TaxesMethodEnum } from '@galabid/gb-dashboard/setup/data-access-taxes'
import { GbRoundHalfEvenService } from '@galabid/shared/utils/helpers'
import * as TaxesActions from 'libs/gb-dashboard/setup/data-access-taxes/src/lib/+state/taxes.actions'
import { GbStripeService } from 'libs/gb-dashboard/shared/data-access-stripe/src/lib/gb-stripe.service'
import {
  BehaviorSubject,
  combineLatest,
  defer,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
} from 'rxjs'
import { CustomerPaymentMethodsFacade } from 'gb-dashboard/checkout/data-access-customer-payment-methods'
import * as CustomerPaymentMethodsActions from 'libs/gb-dashboard/checkout/data-access-customer-payment-methods/src/lib/+state/customer-payment-methods.actions'
import { GbLoaderService, LoaderFacade } from '@galabid/shared/utils/loader'
import { MatDialogRef } from '@angular/material/dialog'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { GbConfirmDialogComponent } from 'libs/shared/ui/src/lib/gb-confirm-dialog/gb-confirm-dialog.component'
import { StripeFacade } from 'gb-dashboard/shared/data-access-stripe'

@Component({
  selector: 'gb-event-ticket-purchase-dialog',
  templateUrl: './gb-event-ticket-purchase-dialog.component.html',
  styleUrls: ['./gb-event-ticket-purchase-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GbEventTicketPurchaseDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroyRef = inject(DestroyRef)
  @ViewChild('purchaseStepper') matStepper: MatStepper | undefined
  selectedStepIndex$ = this.gbEventTicketPurchaseStepperViewController.selectedStepSubject$
  selectedPaymentProcess?: PaymentProcessEnum | null
  PaymentProcessEnum = PaymentProcessEnum
  IconLibrary = IconLibrary
  selectedProfile!: ProfilesEntity
  TaxesMethodEnum = TaxesMethodEnum

  currentSelectedMethod?: PaymentMethodsEntity | null
  selectedEventTicketPackage$ = this.eventTicketPackagesFacade.selectedEventTicketPackage$
  placePurchaseLoaded$ = this.eventTicketPurchasesFacade.placePurchaseLoaded$
  isFreeTicket$ = this.selectedEventTicketPackage$.pipe(map((pkg) => Number(pkg?.price) === 0))
  tax$: Observable<TaxesEntity | undefined> = this.selectedEventTicketPackage$.pipe(
    map((pkg) => pkg?.tax),
    filter((taxUuid): taxUuid is string => !!taxUuid),
    switchMap((taxUuid) => this.taxesFacade.getTaxByUuid$(taxUuid)),
  )

  calculation$: Observable<{ amountValue: number; totalFee: number; totalAmountToPay: number }> = of({
    amountValue: 0,
    totalFee: 0,
    totalAmountToPay: 0,
  })
  ticketForm: FormGroup = this.fb.group({
    quantity: [1, [Validators.required, Validators.min(1)]],
    amount: [0],
    payment_method: [null, [Validators.required]],
    apply_fee: [false],
  })

  private BUTTON_LOADER_KEY = 'stripe-payment-confirm-button-loader'

  buttonLoaded$ = this.loaderFacade.getIsLoading$(this.BUTTON_LOADER_KEY).pipe(
    map((isLoading) => !Boolean(isLoading)),
    startWith(true), // <--- Forces an initial boolean emission
  )

  paymentSessionExpiredSubject$ = new BehaviorSubject<boolean>(false)
  paymentSessionExpired$ = this.paymentSessionExpiredSubject$.asObservable()

  constructor(
    private eventTicketPurchasesFacade: EventTicketPurchasesFacade,
    private eventTicketPackagesFacade: EventTicketPackagesFacade,
    private gbEventTicketPurchaseStepperViewController: GbEventTicketPurchaseStepperViewController,
    private gbStripeService: GbStripeService,
    private gbRoundHalfEvenService: GbRoundHalfEvenService,
    private taxesFacade: TaxesFacade,
    private customerPaymentMethodsFacade: CustomerPaymentMethodsFacade,
    private gbLoaderService: GbLoaderService,
    private loaderFacade: LoaderFacade,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<GbEventTicketPurchaseDialogComponent>,
    private gbDashboardDialogService: GbDashboardDialogService,
    private stripeFacade: StripeFacade,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.gbEventTicketPurchaseStepperViewController.selectedStepSubject$.next(0)

    // Build the grand master calculation pipeline stream
    this.calculation$ = combineLatest([
      this.selectedEventTicketPackage$,
      defer(() => this.ticketForm.valueChanges.pipe(startWith(this.ticketForm.value))),
      this.tax$.pipe(startWith(undefined)),
    ]).pipe(
      map(([ticketPackage, formValues, activeTax]) => {
        const quantity = Number(formValues?.quantity || 1)
        const price = Number(ticketPackage?.price || 0)
        const amountValue = price * quantity

        let taxAmount = 0
        if (activeTax && activeTax.method === TaxesMethodEnum.add) {
          taxAmount = (amountValue * Number(activeTax.tax_rate ?? 0)) / 100
        }

        const baselineAmount = amountValue + taxAmount

        const selectedMethod = formValues?.payment_method
        const applyFeeToggle = !!formValues?.apply_fee

        let totalFee = 0
        let totalAmountToPay = baselineAmount

        if (selectedMethod && baselineAmount > 0) {
          const rawFlatFee = Number(selectedMethod.flat_fee || 0)
          const totalPercentageFee = Number(selectedMethod.percentage_fee || 0) / 100
          const isMandatory = selectedMethod.optional_fee === PaymentMethodFeeTypeEnum.Mandatory

          // Prevent division by zero or negative denominators completely
          const isFormulaSafe = selectedMethod.apply_fee_to_final_charge && totalPercentageFee < 1

          let feeAmount = 0
          if (isFormulaSafe) {
            feeAmount = (baselineAmount + rawFlatFee) / (1 - totalPercentageFee) - baselineAmount
          } else {
            const percentageFeeAmount = baselineAmount * totalPercentageFee
            feeAmount = rawFlatFee + percentageFeeAmount
          }

          totalFee = isNaN(feeAmount) || !isFinite(feeAmount) ? 0 : feeAmount

          if (applyFeeToggle || isMandatory) {
            totalAmountToPay += totalFee
          }
        }

        this.ticketForm.get('amount')?.setValue(amountValue, { emitEvent: false })

        return {
          amountValue,
          totalFee,
          totalAmountToPay: this.gbRoundHalfEvenService.roundHalfEven(totalAmountToPay || 0),
        }
      }),

      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.selectedEventTicketPackage$.pipe(filter(Boolean), take(1)).subscribe((eventTicketPackage) => {
      if (eventTicketPackage.tax) this.loadTax(eventTicketPackage.tax)
    })
  }

  ngAfterViewInit(): void {
    this.gbEventTicketPurchaseStepperViewController.matStepper = this.matStepper!
  }

  ngOnDestroy(): void {
    this.eventTicketPurchasesFacade.dispatch(EventTicketPurchasesActions.clearSelectedEventTicketPurchase())
    this.clearProfileStripePaymentMethods()
  }

  clearProfileStripePaymentMethods(): void {
    this.customerPaymentMethodsFacade.dispatch(CustomerPaymentMethodsActions.clearCustomerPaymentMethods())
    this.gbStripeService.resetStripeElements()
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.gbEventTicketPurchaseStepperViewController.selectedStepSubject$.next(event.selectedIndex)
    setTimeout(() => (event.selectedStep.interacted = false), 0)
  }

  onGuestStepComplete(profile: ProfilesEntity): void {
    this.selectedProfile = profile
    this.syncProfileStripePaymentMethods()
    this.gbEventTicketPurchaseStepperViewController.progressStepper()
  }

  syncProfileStripePaymentMethods(): void {
    this.clearProfileStripePaymentMethods()
    if (this.selectedProfile?.stripe_customer) {
      this.customerPaymentMethodsFacade.dispatch(
        CustomerPaymentMethodsActions.loadCustomerPaymentMethods({
          stripeCustomerUuid: this.selectedProfile.stripe_customer,
        }),
      )
    }
  }

  onPaymentMethodChange(paymentMethod: PaymentMethodsEntity | null): void {
    this.currentSelectedMethod = paymentMethod
    this.selectedPaymentProcess = paymentMethod?.payment_process
  }

  placeEventTicketPurchase(): void {
    this.calculation$.pipe(take(1)).subscribe(({ totalAmountToPay }) => {
      const applyFeeToggle = !!this.ticketForm.get('apply_fee')?.value
      const isMandatory = this.currentSelectedMethod?.optional_fee === PaymentMethodFeeTypeEnum.Mandatory

      const payload = {
        quantity: this.ticketForm.get('quantity')?.value,
        profile: this.selectedProfile?.uuid,
        payment_method: this.currentSelectedMethod?.uuid,
        apply_fee: applyFeeToggle || isMandatory,
        amount: totalAmountToPay,
      }
      this.eventTicketPurchasesFacade.dispatch(EventTicketPurchasesActions.placeEventTicketPurchase({ payload }))
    })
  }

  onConfirmStripePayment(selectedCard: string | null | undefined): void {
    this.gbLoaderService.setLoading(this.BUTTON_LOADER_KEY)
    this.gbStripeService
      .confirmPayment(selectedCard)
      .then((result) => {
        if (!result?.success) return
        this.gbEventTicketPurchaseStepperViewController.progressStepper()
      })
      .catch((err) => console.error('Unexpected error confirming payment', err))
      .finally(() => {
        this.gbLoaderService.setLoadingComplete(this.BUTTON_LOADER_KEY)
      })
  }

  loadTax(taxUuid: string): void {
    this.taxesFacade.dispatch(TaxesActions.loadTax({ taxUuid }))
  }

  closeDialog() {
    // Check your loading stream to ensure it's safe to close
    this.placePurchaseLoaded$.pipe(take(1)).subscribe((loaded) => {
      if (loaded !== false) {
        this.dialogRef.close()
      }
    })
  }

  isConfirmPaymentStep(): boolean {
    return (
      this.currentSelectedMethod?.payment_process === PaymentProcessEnum.Stripe && this.matStepper?.selectedIndex === 2
    )
  }

  onCancel(): void {
    if (this.isConfirmPaymentStep()) {
      this.gbDashboardDialogService
        .getDialogObs(GbConfirmDialogComponent, {
          data: {
            title: 'PAYMENT.CONFIRM.CANCEL.HEADER',
            body: 'PAYMENT.CONFIRM.CANCEL.BODY',
            confirmButtonText: 'PAYMENT.CONFIRM.CANCEL.YES',
            cancelButtonText: 'PAYMENT.CONFIRM.CANCEL.NO',
          },
          maxWidth: '400px',
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          if (res) {
            this.dispatchCancelPayment()
            this.closeDialog()
          }
        })
    } else {
      this.closeDialog()
    }
  }

  onPaymentSessionExpired(): void {
    this.dispatchCancelPayment()
    this.paymentSessionExpiredSubject$.next(true)
    this.cdr.markForCheck()
  }

  dispatchCancelPayment(): void {
    this.stripeFacade.dispatch(StripeActions.cancelPayment())
  }
}
